<template>
  <div class="products-tab">
    <div class="card">
      <!-- Фильтры и действия -->
      <div class="flex gap-3 mb-4 flex-wrap">
        <div class="flex-1">
          <IconField iconPosition="left">
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText
              v-model="filters.search"
              placeholder="Поиск товаров..."
              @input="loadProducts"
              class="w-full"
            />
          </IconField>
        </div>
        <div>
          <Dropdown
            v-model="filters.status"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Все статусы"
            @change="loadProducts"
          />
        </div>
        <div>
          <Dropdown
            v-model="filters.sync_status"
            :options="syncStatusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Статус синхронизации"
            @change="loadProducts"
          />
        </div>
        <div>
          <Button
            icon="pi pi-sync"
            label="Синхронизировать выбранные"
            class="p-button-primary"
            :disabled="!selectedProducts || selectedProducts.length === 0 || isAnySyncRunning"
            :loading="isSyncing"
            @click="syncSelected"
            v-tooltip.top="isAnySyncRunning ? 'Дождитесь завершения текущей синхронизации' : ''"
          />
        </div>
        <div>
          <Button
            icon="pi pi-sync"
            label="Синхронизировать все товары"
            class="p-button-success"
            :disabled="isAnySyncRunning && !isSyncingAll"
            :loading="isSyncingAll"
            @click="syncAllActive"
            v-tooltip.top="isAnySyncRunning && !isSyncingAll ? 'Дождитесь завершения текущей синхронизации' : ''"
          />
        </div>
        <div>
          <Button
            icon="pi pi-exclamation-triangle"
            label="Синхронизировать только ошибки"
            class="p-button-danger"
            :disabled="isAnySyncRunning && !isSyncingErrors"
            :loading="isSyncingErrors"
            @click="syncOnlyErrors"
            v-tooltip.top="isAnySyncRunning && !isSyncingErrors ? 'Дождитесь завершения текущей синхронизации' : 'Синхронизировать только товары со статусом error или pending'"
          />
        </div>
        <div>
          <Button
            icon="pi pi-tag"
            label="Синхронизировать по брендам"
            class="p-button-info"
            :disabled="isAnySyncRunning && !isSyncingByBrands"
            :loading="isSyncingByBrands"
            @click="openBrandsSyncDialog"
            v-tooltip.top="isAnySyncRunning && !isSyncingByBrands ? 'Дождитесь завершения текущей синхронизации' : 'Выбрать бренды для синхронизации'"
          />
        </div>
      </div>

      <!-- Таблица товаров -->
      <DataTable
        v-model:selection="selectedProducts"
        :value="products"
        :loading="loading"
        paginator
        :rows="20"
        :rowsPerPageOptions="[10, 20, 50, 100]"
        :totalRecords="totalRecords"
        :lazy="true"
        @page="onPage"
        responsiveLayout="scroll"
        stripedRows
      >
        <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
        <Column field="part_number" header="Артикул" style="min-width: 150px"></Column>
        <Column field="part_name" header="Название" style="min-width: 200px"></Column>
        <Column field="brand" header="Бренд" style="width: 120px"></Column>
        <Column field="producer" header="Производитель" style="width: 140px"></Column>
        <Column field="status" header="Статус" style="width: 100px">
          <template #body="slotProps">
            <Tag
              :value="slotProps.data.status"
              :severity="slotProps.data.status === 'ACTIVE' ? 'success' : 'danger'"
            />
          </template>
        </Column>
        <Column field="sync_status" header="Синхронизация" style="width: 140px">
          <template #body="slotProps">
            <Tag
              :value="getSyncStatusLabel(slotProps.data.sync_status)"
              :severity="getSyncStatusSeverity(slotProps.data.sync_status)"
            />
          </template>
        </Column>
        <Column header="Действия" style="width: 150px">
          <template #body="slotProps">
            <div class="flex gap-2">
              <Button
                icon="pi pi-eye"
                class="p-button-sm p-button-text"
                v-tooltip.top="'Предпросмотр'"
                @click="previewProduct(slotProps.data)"
              />
              <Button
                icon="pi pi-sync"
                class="p-button-sm p-button-text p-button-primary"
                v-tooltip.top="'Синхронизировать'"
                :loading="syncingProductIds.has(slotProps.data.id)"
                @click="syncProduct(slotProps.data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Диалог предпросмотра синхронизации -->
    <Dialog
      v-model:visible="showPreviewDialog"
      header="Предпросмотр синхронизации"
      :modal="true"
      :style="{ width: '800px' }"
    >
      <div v-if="previewData" class="preview-content">
        <h4>Товар: {{ previewData.part_number || '-' }} - {{ previewData.part_name || '-' }}</h4>
        
        <!-- Категории -->
        <div class="section mt-4">
          <h5>Категории</h5>
          <div v-if="previewData.categories && previewData.categories.length > 0">
            <ul>
              <li v-for="(cat, idx) in previewData.categories" :key="idx">
                <strong>{{ cat.path }}</strong>
                <span v-if="cat.exists" class="text-green-600"> (существует)</span>
                <span v-else class="text-blue-600"> (будет создана)</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Параметры товара -->
        <div class="section mt-4">
          <h5>Параметры</h5>
          <DataTable :value="previewData.fields" size="small">
            <Column field="name" header="Параметр">
              <template #body="slotProps">
                <span>{{ slotProps.data.name }}</span>
              </template>
            </Column>
            <Column field="value" header="Значение">
              <template #body="slotProps">
                <span v-if="slotProps.data.is_filled" class="text-green-600">
                  {{ slotProps.data.value }}
                </span>
                <span v-else class="text-red-600 font-bold">
                  Не заполнено
                </span>
              </template>
            </Column>
          </DataTable>
        </div>

        <!-- Недостающие поля -->
        <div v-if="previewData.missing_fields && previewData.missing_fields.length > 0" class="section mt-4">
          <h5 class="text-red-600">⚠ Недостающие поля</h5>
          <ul class="text-red-600">
            <li v-for="(field, idx) in previewData.missing_fields" :key="idx">
              {{ field.field }}: {{ field.reason }}
            </li>
          </ul>
        </div>

        <!-- Сообщение о готовности -->
        <div class="mt-4">
          <Message
            v-if="previewData.can_sync"
            severity="success"
            :closable="false"
          >
            Товар готов к синхронизации
          </Message>
          <Message
            v-else
            severity="warn"
            :closable="false"
          >
            {{ previewData.sync_blocked_reason || 'Товар не может быть синхронизирован из-за недостающих данных' }}
          </Message>
        </div>
      </div>

      <template #footer>
        <Button label="Закрыть" icon="pi pi-times" class="p-button-text" @click="showPreviewDialog = false" />
        <Button
          v-if="previewData && previewData.can_sync"
          label="Синхронизировать"
          icon="pi pi-sync"
          class="p-button-primary"
          @click="confirmSyncFromPreview"
        />
      </template>
    </Dialog>

    <!-- Прогресс синхронизации -->
    <SyncProgressDialog
      v-model:visible="showProgressDialog"
      :store-id="props.storeId"
      :is-admin="isAdmin"
      @completed="onSyncCompleted"
    />

    <!-- Диалог подтверждения синхронизации всех товаров -->
    <Dialog
      v-model:visible="showSyncAllDialog"
      header="Подтверждение синхронизации"
      :modal="true"
      :style="{ width: '480px' }"
    >
      <div class="flex items-start gap-3 mb-4">
        <i class="pi pi-exclamation-triangle text-2xl text-yellow-500 mt-1" />
        <p>Вы действительно хотите синхронизировать все товары (активные и архивные)? Это может занять некоторое время.</p>
      </div>

      <div class="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
        <Checkbox
          v-model="forceAllSync"
          :binary="true"
          inputId="forceAllSync"
        />
        <label for="forceAllSync" class="cursor-pointer">
          <span class="font-medium text-orange-800">Принудительное обновление всех товаров</span>
          <p class="text-xs text-orange-600 mt-1">
            Обновить все товары, даже если они не изменялись с последней синхронизации.
            Используйте для повторной отправки изображений или если данные на сайте расходятся с базой.
          </p>
        </label>
      </div>

      <template #footer>
        <Button
          label="Отмена"
          class="p-button-text"
          @click="showSyncAllDialog = false"
        />
        <Button
          label="Да, начать"
          icon="pi pi-sync"
          class="p-button-success"
          @click="confirmSyncAll"
        />
      </template>
    </Dialog>

    <!-- Диалог выбора брендов для синхронизации -->
    <Dialog
      v-model:visible="showBrandsSyncDialog"
      header="Синхронизация по брендам"
      :modal="true"
      :style="{ width: '700px' }"
      :closable="!isSyncingByBrands"
    >
      <div class="brands-sync-content">
        <Message severity="info" :closable="false" class="mb-4">
          <p><strong>Синхронизация по брендам</strong> позволяет синхронизировать только товары выбранных брендов.</p>
          <p class="mt-2">Выберите один или несколько брендов и нажмите "Начать синхронизацию".</p>
        </Message>

        <!-- Загрузка брендов -->
        <div v-if="loadingBrands" class="text-center py-4">
          <ProgressBar mode="indeterminate" style="height: 6px" />
          <p class="mt-2 text-sm text-gray-600">Загрузка списка брендов...</p>
        </div>

        <!-- Список брендов -->
        <div v-else>
          <!-- Поиск по брендам -->
          <div class="mb-3">
            <IconField iconPosition="left">
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText
                v-model="brandSearchQuery"
                placeholder="Поиск брендов..."
                class="w-full"
              />
            </IconField>
          </div>

          <!-- Счётчик выбранных -->
          <div class="flex justify-between items-center mb-3">
            <span class="text-sm text-gray-600">
              Выбрано: <strong>{{ selectedBrands.length }}</strong> из {{ filteredBrands.length }} брендов
              <span v-if="selectedBrandsProductCount > 0" class="text-blue-600">
                ({{ selectedBrandsProductCount.toLocaleString() }} товаров)
              </span>
            </span>
            <div class="flex gap-2">
              <Button
                label="Выбрать все"
                class="p-button-sm p-button-text"
                @click="selectAllBrands"
                :disabled="filteredBrands.length === 0"
              />
              <Button
                label="Сбросить"
                class="p-button-sm p-button-text p-button-secondary"
                @click="clearBrandSelection"
                :disabled="selectedBrands.length === 0"
              />
            </div>
          </div>

          <!-- Опция принудительного обновления -->
          <div class="flex items-center gap-2 mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <Checkbox
              v-model="forceAllBrands"
              :binary="true"
              inputId="forceAllBrands"
            />
            <label for="forceAllBrands" class="cursor-pointer">
              <span class="font-medium text-orange-800">Принудительное обновление всех товаров</span>
              <p class="text-xs text-orange-600 mt-1">
                Обновить все товары выбранных брендов, даже если они не изменялись. 
                Используйте, если товары были удалены из сайта через админку.
              </p>
            </label>
          </div>

          <!-- Список брендов с чекбоксами -->
          <div class="brands-list" style="max-height: 400px; overflow-y: auto;">
            <div
              v-for="brand in filteredBrands"
              :key="brand.brand"
              class="brand-item flex items-center gap-2 py-2 px-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
              @click="toggleBrand(brand.brand)"
            >
              <Checkbox
                :modelValue="selectedBrands.includes(brand.brand)"
                :binary="true"
                @click.stop="toggleBrand(brand.brand)"
              />
              <span class="flex-1 font-medium">{{ brand.brand }}</span>
              <Tag
                :value="`${brand.count} товаров`"
                severity="secondary"
                class="text-xs"
              />
            </div>
          </div>

          <!-- Пустой список -->
          <div v-if="filteredBrands.length === 0" class="text-center py-4 text-gray-500">
            <i class="pi pi-inbox text-4xl mb-2"></i>
            <p>Бренды не найдены</p>
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          label="Отмена"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeBrandsSyncDialog"
          :disabled="isSyncingByBrands"
        />
        <Button
          label="Начать синхронизацию"
          icon="pi pi-sync"
          class="p-button-info"
          @click="syncByBrands"
          :loading="isSyncingByBrands"
          :disabled="selectedBrands.length === 0"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Dropdown from 'primevue/dropdown'
import Dialog from 'primevue/dialog'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import ProgressBar from 'primevue/progressbar'
import Checkbox from 'primevue/checkbox'
import { useConfirm } from 'primevue/useconfirm'
import apiService from '../../services/apiService'
import SyncProgressDialog from './SyncProgressDialog.vue'

// Props
const props = defineProps({
  storeId: {
    type: Number,
    required: true
  },
  isStoreSyncing: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['sync-status-change'])

const toast = useToast()
const confirmService = useConfirm()

// State
const loading = ref(false)
const isSyncing = ref(false)
const isSyncingAll = ref(false)
const isSyncingErrors = ref(false)
const isSyncingByBrands = ref(false)

// Флаг блокировки всех кнопок синхронизации когда идёт любая синхронизация
const isAnySyncRunning = computed(() => {
  return isSyncing.value || isSyncingAll.value || isSyncingErrors.value || isSyncingByBrands.value || props.isStoreSyncing
})

// Локальный статус синхронизации для уведомления родителя
const isLocalSyncRunning = computed(() => {
  return isSyncing.value || isSyncingAll.value || isSyncingErrors.value || isSyncingByBrands.value
})

watch(isLocalSyncRunning, (newValue) => {
  emit('sync-status-change', newValue)
})

const products = ref([])
const selectedProducts = ref([])
const totalRecords = ref(0)
const syncingProductIds = ref(new Set())

// Brands sync state
const showBrandsSyncDialog = ref(false)
const loadingBrands = ref(false)
const availableBrands = ref([])
const selectedBrands = ref([])
const brandSearchQuery = ref('')
const forceAllBrands = ref(false)  // Принудительное обновление всех товаров бренда

// Sync All state
const showSyncAllDialog = ref(false)
const forceAllSync = ref(false)

const filters = ref({
  search: '',
  status: null,
  sync_status: null
})

const pagination = ref({
  page: 0,
  rows: 20
})

const statusOptions = [
  { label: 'Все', value: null },
  { label: 'Активные', value: 'ACTIVE' },
  { label: 'Архивные', value: 'ARCHIVE' }
]

const syncStatusOptions = [
  { label: 'Все', value: null },
  { label: 'Не синхронизированы', value: 'not_synced' },
  { label: 'Синхронизированы', value: 'synced' },
  { label: 'Ошибка', value: 'error' },
  { label: 'Недостающие данные', value: 'missing_data' }
]

const showPreviewDialog = ref(false)
const previewData = ref(null)

const showProgressDialog = ref(false)

// Проверка роли для скрытия кнопок pause/resume/stop
const isAdmin = computed(() => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
    return userInfo.role === 'admin'
  } catch { return false }
})

// Фильтрация брендов по поисковому запросу
const filteredBrands = computed(() => {
  if (!brandSearchQuery.value) {
    return availableBrands.value
  }
  const query = brandSearchQuery.value.toLowerCase()
  return availableBrands.value.filter(b =>
    b.brand.toLowerCase().includes(query)
  )
})

// Подсчёт товаров в выбранных брендах
const selectedBrandsProductCount = computed(() => {
  return availableBrands.value
    .filter(b => selectedBrands.value.includes(b.brand))
    .reduce((sum, b) => sum + b.count, 0)
})

// Загрузка товаров
const loadProducts = async () => {
  loading.value = true
  try {
    const params = {
      limit: pagination.value.rows,
      offset: pagination.value.page * pagination.value.rows
    }
    
    if (filters.value.search) params.search = filters.value.search
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.sync_status) params.sync_status = filters.value.sync_status
    
  const response = await apiService.get('/sync/products', { 
    params: { ...params, store_id: props.storeId }
  })
    products.value = response.data.products
    totalRecords.value = response.data.total
  } catch (error) {
    console.error('Ошибка загрузки товаров:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить товары',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

// Пагинация
const onPage = (event) => {
  pagination.value.page = event.page
  pagination.value.rows = event.rows
  loadProducts()
}

// Получить метку статуса синхронизации
const getSyncStatusLabel = (status) => {
  const map = {
    'not_synced': 'Не синхронизирован',
    'synced': 'Синхронизирован',
    'error': 'Ошибка',
    'missing_data': 'Недостающие данные'
  }
  return map[status] || status
}

const getSyncStatusSeverity = (status) => {
  const map = {
    'not_synced': 'info',
    'synced': 'success',
    'error': 'danger',
    'missing_data': 'warning'
  }
  return map[status] || 'info'
}

// Предпросмотр товара
const previewProduct = async (product) => {
  try {
  const response = await apiService.get(`/sync/products/${product.id}/preview`, {
    params: { store_id: props.storeId }
  })
    previewData.value = response.data
    showPreviewDialog.value = true
  } catch (error) {
    console.error('Ошибка предпросмотра:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить предпросмотр',
      life: 3000
    })
  }
}

// Синхронизация одного товара
const syncProduct = async (product) => {
  syncingProductIds.value.add(product.id)
  try {
    const response = await apiService.post('/sync/products/sync', {
      product_ids: [product.id]
    }, {
      params: { store_id: props.storeId }
    })
    
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: response.data.message || 'Товар синхронизирован',
      life: 3000
    })
    
    await loadProducts()
  } catch (error) {
    console.error('Ошибка синхронизации:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось синхронизировать товар',
      life: 5000
    })
  } finally {
    syncingProductIds.value.delete(product.id)
  }
}

// Синхронизация выбранных товаров
const syncSelected = () => {
  if (!selectedProducts.value || selectedProducts.value.length === 0) return

  confirmService.require({
    message: `Вы действительно хотите синхронизировать ${selectedProducts.value.length} выбранных товаров?`,
    header: 'Подтверждение синхронизации',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Да, начать',
    rejectLabel: 'Отмена',
    accept: async () => {
      try {
        // ШАГ 1: Получить информацию о магазине для проверки локали
        const storeInfo = await apiService.getStoreDetails(props.storeId)

        // ШАГ 2: Для нерусских магазинов - проверить переводы
        if (storeInfo.locale && storeInfo.locale !== 'en') {
          const translationCheck = await apiService.checkTranslations(props.storeId)

          if (translationCheck.translations_needed) {
            // Переводы отсутствуют - показать предупреждение
            const proceed = window.confirm(
              `⚠️ Обнаружено ${translationCheck.total_missing} недостающих переводов для ${storeInfo.locale}.\n\n` +
              `Рекомендуется сначала создать переводы через кнопку "${getLocaleFlag(storeInfo.locale)} Переводы" в заголовке.\n\n` +
              `Продолжить синхронизацию без переводов? (Будут использованы оригинальные значения на русском)`
            )

            if (!proceed) {
              toast.add({
                severity: 'warn',
                summary: 'Синхронизация отменена',
                detail: 'Создайте переводы перед синхронизацией',
                life: 5000
              })
              return
            }
          }
        }

        // ШАГ 3: Начать синхронизацию
        isSyncing.value = true
        showProgressDialog.value = true
      
        const productIds = selectedProducts.value.map(p => p.id)
        await apiService.post('/sync/products/sync', {
          product_ids: productIds
        }, {
          params: { store_id: props.storeId }
        })
        
        toast.add({
          severity: 'info',
          summary: 'Синхронизация запущена',
          detail: 'Следите за прогрессом в диалоге',
          life: 3000
        })
        
        selectedProducts.value = []
      } catch (error) {
        console.error('Ошибка синхронизации:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: error.response?.data?.detail || 'Не удалось синхронизировать товары',
          life: 5000
        })
        showProgressDialog.value = false
      } finally {
        isSyncing.value = false
        // Не закрываем диалог - он сам закроется после завершения
      }
    }
  })
}

// Синхронизация всех товаров (активных и архивных)
const syncAllActive = () => {
  forceAllSync.value = false
  showSyncAllDialog.value = true
}

const confirmSyncAll = async () => {
  showSyncAllDialog.value = false
  try {
    // ШАГ 1: Получить информацию о магазине для проверки локали
    const storeInfo = await apiService.getStoreDetails(props.storeId)

    // ШАГ 2: Для нерусских магазинов - проверить переводы
    if (storeInfo.locale && storeInfo.locale !== 'en') {
      const translationCheck = await apiService.checkTranslations(props.storeId)

      if (translationCheck.translations_needed) {
        const proceed = window.confirm(
          `⚠️ Обнаружено ${translationCheck.total_missing} недостающих переводов для ${storeInfo.locale}.\n\n` +
          `Рекомендуется сначала создать переводы через кнопку "${getLocaleFlag(storeInfo.locale)} Переводы" в заголовке.\n\n` +
          `Продолжить синхронизацию без переводов? (Будут использованы оригинальные значения на русском)`
        )

        if (!proceed) {
          toast.add({
            severity: 'warn',
            summary: 'Синхронизация отменена',
            detail: 'Создайте переводы перед синхронизацией',
            life: 5000
          })
          return
        }
      }
    }

    // ШАГ 3: Начать синхронизацию
    isSyncingAll.value = true
    showProgressDialog.value = true

    await apiService.post('/sync/products/sync', {
      force_all: forceAllSync.value
    }, {
      params: { store_id: props.storeId }
    })

    toast.add({
      severity: 'info',
      summary: 'Синхронизация запущена',
      detail: forceAllSync.value
        ? 'Принудительная синхронизация всех товаров запущена'
        : 'Следите за прогрессом в диалоге',
      life: 3000
    })
  } catch (error) {
    console.error('Ошибка синхронизации:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось синхронизировать товары',
      life: 5000
    })
    showProgressDialog.value = false
  } finally {
    isSyncingAll.value = false
  }
}

// Синхронизация только товаров с ошибками
const syncOnlyErrors = () => {
  confirmService.require({
    message: 'Вы действительно хотите синхронизировать только товары с ошибками?',
    header: 'Подтверждение синхронизации',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Да, начать',
    rejectLabel: 'Отмена',
    accept: async () => {
      try {
        // ШАГ 1: Получить информацию о магазине для проверки локали
        const storeInfo = await apiService.getStoreDetails(props.storeId)

        // ШАГ 2: Для нерусских магазинов - проверить переводы
        if (storeInfo.locale && storeInfo.locale !== 'en') {
          const translationCheck = await apiService.checkTranslations(props.storeId)

          if (translationCheck.translations_needed) {
            // Переводы отсутствуют - показать предупреждение
            const proceed = window.confirm(
              `⚠️ Обнаружено ${translationCheck.total_missing} недостающих переводов для ${storeInfo.locale}.\n\n` +
              `Рекомендуется сначала создать переводы через кнопку "${getLocaleFlag(storeInfo.locale)} Переводы" в заголовке.\n\n` +
              `Продолжить синхронизацию без переводов? (Будут использованы оригинальные значения на русском)`
            )

            if (!proceed) {
              toast.add({
                severity: 'warn',
                summary: 'Синхронизация отменена',
                detail: 'Создайте переводы перед синхронизацией',
                life: 5000
              })
              return
            }
          }
        }

        // ШАГ 3: Начать синхронизацию
        isSyncingErrors.value = true
        showProgressDialog.value = true

        await apiService.post('/sync/products/sync', {
          only_errors: true
        }, {
          params: { store_id: props.storeId }
        })

        toast.add({
          severity: 'info',
          summary: 'Синхронизация запущена',
          detail: 'Следите за прогрессом в диалоге',
          life: 3000
        })
      } catch (error) {
        console.error('Ошибка синхронизации:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: error.response?.data?.detail || 'Не удалось синхронизировать товары',
          life: 5000
        })
        showProgressDialog.value = false
      } finally {
        isSyncingErrors.value = false
        // Не закрываем диалог - он сам закроется после завершения
      }
    }
  })
}

// === Синхронизация по брендам ===

// Открыть диалог выбора брендов
const openBrandsSyncDialog = async () => {
  showBrandsSyncDialog.value = true
  brandSearchQuery.value = ''
  selectedBrands.value = []
  forceAllBrands.value = false  // Сброс чекбокса принудительного обновления
  await loadBrands()
}

// Закрыть диалог выбора брендов
const closeBrandsSyncDialog = () => {
  showBrandsSyncDialog.value = false
  selectedBrands.value = []
  brandSearchQuery.value = ''
  forceAllBrands.value = false  // Сброс чекбокса принудительного обновления
}

// Загрузить список брендов
const loadBrands = async () => {
  loadingBrands.value = true
  try {
    const response = await apiService.getBrandsForSync(props.storeId)
    availableBrands.value = response.brands || []
  } catch (error) {
    console.error('Ошибка загрузки брендов:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить список брендов',
      life: 3000
    })
    availableBrands.value = []
  } finally {
    loadingBrands.value = false
  }
}

// Переключить выбор бренда
const toggleBrand = (brandName) => {
  const index = selectedBrands.value.indexOf(brandName)
  if (index === -1) {
    selectedBrands.value.push(brandName)
  } else {
    selectedBrands.value.splice(index, 1)
  }
}

// Выбрать все отфильтрованные бренды
const selectAllBrands = () => {
  selectedBrands.value = filteredBrands.value.map(b => b.brand)
}

// Сбросить выбор брендов
const clearBrandSelection = () => {
  selectedBrands.value = []
}

// Запустить синхронизацию по выбранным брендам
const syncByBrands = () => {
  if (selectedBrands.value.length === 0) {
    toast.add({
      severity: 'warn',
      summary: 'Предупреждение',
      detail: 'Выберите хотя бы один бренд',
      life: 3000
    })
    return
  }

  confirmService.require({
    message: `Вы действительно хотите синхронизировать товары ${selectedBrands.value.length} выбранных брендов?`,
    header: 'Подтверждение синхронизации',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Да, начать',
    rejectLabel: 'Отмена',
    accept: async () => {
      try {
        // ШАГ 1: Получить информацию о магазине для проверки локали
        const storeInfo = await apiService.getStoreDetails(props.storeId)

        // ШАГ 2: Для нерусских магазинов - проверить переводы
        if (storeInfo.locale && storeInfo.locale !== 'en') {
          const translationCheck = await apiService.checkTranslations(props.storeId)

          if (translationCheck.translations_needed) {
            // Переводы отсутствуют - показать предупреждение
            const proceed = window.confirm(
              `⚠️ Обнаружено ${translationCheck.total_missing} недостающих переводов для ${storeInfo.locale}.\n\n` +
              `Рекомендуется сначала создать переводы через кнопку "${getLocaleFlag(storeInfo.locale)} Переводы" в заголовке.\n\n` +
              `Продолжить синхронизацию без переводов? (Будут использованы оригинальные значения на русском)`
            )

            if (!proceed) {
              toast.add({
                severity: 'warn',
                summary: 'Синхронизация отменена',
                detail: 'Создайте переводы перед синхронизацией',
                life: 5000
              })
              return
            }
          }
        }

        // ШАГ 3: Начать синхронизацию
        isSyncingByBrands.value = true
        showProgressDialog.value = true

        await apiService.syncProductsByBrands(
          props.storeId,
          selectedBrands.value,
          forceAllBrands.value  // Принудительное обновление всех товаров
        )

        toast.add({
          severity: 'info',
          summary: 'Синхронизация запущена',
          detail: forceAllBrands.value 
            ? 'Принудительное обновление всех товаров. Следите за прогрессом в диалоге.'
            : 'Следите за прогрессом в диалоге',
          life: 3000
        })

        closeBrandsSyncDialog()
      } catch (error) {
        console.error('Ошибка синхронизации по брендам:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: error.response?.data?.detail || 'Не удалось запустить синхронизацию',
          life: 5000
        })
        showProgressDialog.value = false
      } finally {
        isSyncingByBrands.value = false
        // Не закрываем диалог - он сам закроется после завершения
      }
    }
  })
}

// Обработчик завершения синхронизации
const onSyncCompleted = async (progress) => {
  // Показать уведомление о завершении
  const summary = progress?.summary || {}
  const successTotal = summary.success || 0
  const failedTotal = summary.failed || 0
  
  toast.add({
    severity: failedTotal > 0 ? 'warn' : 'success',
    summary: 'Синхронизация завершена',
    detail: `Успешно: ${successTotal}, ошибок: ${failedTotal}`,
    life: 5000
  })
  
  // Перезагрузить список товаров
  await loadProducts()
}

// Утилита для получения флага языка
const getLocaleFlag = (locale) => {
  const flags = {
    'ru': '🇷🇺',
    'en': '🇬🇧',
    'it': '🇮🇹',
    'de': '🇩🇪',
    'es': '🇪🇸'
  }
  return flags[locale] || '🌍'
}

// Подтверждение синхронизации из предпросмотра
const confirmSyncFromPreview = async () => {
  if (!previewData.value || !previewData.value.product_id) return

  showPreviewDialog.value = false

  // Найти товар в списке по product_id
  const product = products.value.find(p => p.id === previewData.value.product_id)
  if (product) {
    await syncProduct(product)
  }
}

// Отслеживание изменения storeId
watch(() => props.storeId, () => {
  loadProducts()
})

// Проверка, идет ли синхронизация для этого магазина
const checkIfSyncing = async () => {
  try {
    const response = await apiService.get(`/sync/products/is-syncing/${props.storeId}`)
    if (response.data.is_syncing) {
      // Если синхронизация уже идёт - показать диалог прогресса
      showProgressDialog.value = true
    }
  } catch (error) {
    console.error('Ошибка проверки статуса синхронизации:', error)
  }
}

// Проверка наличия незавершённой (приостановленной) синхронизации после перезагрузки
const checkForPausedCheckpoint = async () => {
  try {
    const response = await apiService.get(`/sync/products/pause-state/${props.storeId}`)
    if (response.data.has_checkpoint && !response.data.is_paused) {
      // Есть checkpoint от прерванной синхронизации (перезагрузка/рестарт)
      const info = response.data.checkpoint_info
      const message = info
        ? `Найдена прерванная синхронизация (этап: ${info.stage || 'неизвестно'}, обработано: ${info.processed || 0}). Вы можете продолжить синхронизацию.`
        : 'Найдена прерванная синхронизация. Вы можете продолжить синхронизацию.'
      toast.add({
        severity: 'warn',
        summary: 'Незавершённая синхронизация',
        detail: message,
        life: 15000
      })
    }
  } catch (error) {
    // Не критичная ошибка — молча игнорируем
    console.debug('Проверка checkpoint:', error?.message)
  }
}

onMounted(() => {
  loadProducts()
  checkIfSyncing()
  checkForPausedCheckpoint()
})
</script>

<style scoped>
.products-tab {
  min-height: 600px;
}

.card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.preview-content .section {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
}

.progress-content {
  padding: 1rem 0;
}

/* Стили для диалога выбора брендов */
.brands-sync-content {
  min-height: 200px;
}

.brands-list {
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.brand-item {
  transition: background-color 0.15s ease;
}

.brand-item:last-child {
  border-bottom: none;
}

.brand-item:hover {
  background-color: #f8f9fa;
}
</style>
